import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import Select from '../../Select/Select';

/**
 * Компонент-обертка для интеграции компонента Select с библиотекой react-hook-form.
 * Упрощает использование Select в формах, управляемых через react-hook-form.
 *
 * @param {string} name - Имя поля в форме (обязательный параметр)
 * @param {string} label - Текст метки (лейбла) для селекта
 * @param {Object[]} options - Массив опций для выбора
 * @param {string} [placeholder='Не выбрано'] - Текст для отображения, когда ничего не выбрано
 * @param {boolean} [disabled=false] - Флаг, определяющий, отключен ли компонент
 * @param {boolean} [multiple=false] - Режим множественного выбора
 * @param {boolean} [clearable=true] - Возможность очистить выбранное значение
 * @param {boolean} [searchable=false] - Включение поиска по опциям
 * @param {string} [valueKey='id'] - Имя поля в объекте options, которое используется как значение опции
 * @param {string} [titleKey='name'] - Имя поля в объекте options, которое используется как текст опции
 * @param {string[]} [searchFields=[]] - Массив полей для поиска
 * @param {function} [renderOption] - Функция для кастомного рендеринга опций
 * @param {function} [renderValue] - Функция для кастомного рендеринга выбранного значения
 * @param {string} [className] - Дополнительные CSS-классы для контейнера
 * @param {Object} [rules] - Правила валидации для react-hook-form
 * @param {string} [arrowColor] - Цвет стрелки раскрытия списка
 * @param {boolean} hideErrorBlock - скрыть блок для ошибки
 * @param {boolean} [required=false] - Флаг указывающий на обязательность заполнения поля
 *
 * @example
 * // Базовое использование с обязательным полем
 * <FormSelect
 *   name="category"
 *   label="Выберите категорию"
 *   options={[
 *     { id: 1, name: 'Категория 1' },
 *     { id: 2, name: 'Категория 2' }
 *   ]}
 *   rules={{ required: 'Необходимо выбрать категорию' }}
 * />
 *
 * @example
 * // Множественный выбор с поиском
 * <FormSelect
 *   name="tags"
 *   label="Выберите теги"
 *   options={[
 *     { id: 1, name: 'JavaScript' },
 *     { id: 2, name: 'React' },
 *     { id: 3, name: 'Node.js' }
 *   ]}
 *   multiple={true}
 *   searchable={true}
 *   searchFields={['name']}
 *   rules={{
 *     validate: value => value.length > 0 || 'Выберите хотя бы один тег'
 *   }}
 * />
 *
 * @example
 * // С кастомным рендерингом опций
 * <FormSelect
 *   name="user"
 *   label="Выберите пользователя"
 *   options={users}
 *   valueKey="id"
 *   titleKey="username"
 *   renderOption={({ option, isSelected }) => (
 *     <div className={isSelected ? 'selected-user' : 'user-option'}>
 *       <img src={option.avatar} alt="avatar" />
 *       <span>{option.username}</span>
 *       <span className="user-email">{option.email}</span>
 *     </div>
 *   )}
 * />
 *
 * @example
 * // С кастомным рендерингом выбранного значения
 * <FormSelect
 *   name="location"
 *   label="Выберите местоположение"
 *   options={locations}
 *   renderValue={({ value, options, valueKey, titleKey }) => {
 *     const selected = options.find(opt => opt[valueKey] === value);
 *     return selected ? (
 *       <div className="location-display">
 *         <Icon src="/icons/location.svg" />
 *         <span>{selected.city}, {selected.country}</span>
 *       </div>
 *     ) : null;
 *   }}
 * />
 *
 * @example
 * // В контексте полной формы с react-hook-form
 * const RegisterForm = () => {
 *   const methods = useForm({
 *     defaultValues: {
 *       department: null,
 *       roles: []
 *     }
 *   });
 *
 *   const onSubmit = data => console.log(data);
 *
 *   return (
 *     <FormProvider {...methods}>
 *       <form onSubmit={methods.handleSubmit(onSubmit)}>
 *         <FormSelect
 *           name="department"
 *           label="Отдел"
 *           options={departments}
 *           rules={{ required: 'Выберите отдел' }}
 *         />
 *
 *         <FormSelect
 *           name="roles"
 *           label="Роли пользователя"
 *           options={roles}
 *           multiple={true}
 *           searchable={true}
 *         />
 *
 *         <button type="submit">Сохранить</button>
 *       </form>
 *     </FormProvider>
 *   );
 * };
 */
const FormSelect = ({
  name,
  label,
  options = [],
  placeholder = 'Не выбрано',
  disabled = false,
  multiple = false,
  required = false,
  clearable = true,
  searchable = false,
  valueKey = 'id',
  titleKey = 'name',
  searchFields = [],
  renderOption,
  renderValue,
  className,
  rules = {},
  arrowColor,
  hideErrorBlock = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const formRules = useMemo(() => {
    let defaultRules = {};
    if (required) {
      defaultRules.required = 'Поле обязательно для заполнения';
    }

    return { ...defaultRules, ...rules };
  }, [required, rules]);

  return (
    <Controller
      name={name}
      control={control}
      rules={formRules}
      render={({ field: { onChange, value } }) => (
        <Select
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          multiple={multiple}
          clearable={clearable}
          searchable={searchable}
          valueKey={valueKey}
          titleKey={titleKey}
          required={required}
          searchFields={searchFields}
          renderOption={renderOption}
          renderValue={renderValue}
          className={className}
          label={label}
          arrowColor={arrowColor}
          errorMessage={errors[name]}
          isFormValidate={!!errors[name]}
          hideErrorBlock={hideErrorBlock}
        />
      )}
    />
  );
};

FormSelect.propTypes = {
  /**
   * Имя поля в форме (обязательный параметр)
   */
  name: PropTypes.string.isRequired,

  /**
   * Текст метки (лейбла) для селекта
   */
  label: PropTypes.string,

  /**
   * Массив опций для выбора
   */
  options: PropTypes.arrayOf(PropTypes.object),

  /**
   * Текст для отображения когда ничего не выбрано
   */
  placeholder: PropTypes.string,

  /**
   * Отключает компонент
   */
  disabled: PropTypes.bool,

  /**
   * Разрешает выбор нескольких значений
   */
  multiple: PropTypes.bool,

  /**
   * Позволяет очистить выбранное значение
   */
  clearable: PropTypes.bool,

  /**
   * Включает строку поиска
   */
  searchable: PropTypes.bool,

  /**
   * Имя поля в объекте options, которое используется как значение опции
   */
  valueKey: PropTypes.string,

  /**
   * Имя поля в объекте options, которое используется как текст опции
   */
  titleKey: PropTypes.string,

  /**
   * Массив полей для поиска (если не указан, используется только titleKey)
   */
  searchFields: PropTypes.arrayOf(PropTypes.string),

  /**
   * Функция для кастомного рендеринга опций
   */
  renderOption: PropTypes.func,

  /**
   * Функция для кастомного рендеринга выбранного значения
   */
  renderValue: PropTypes.func,

  /**
   * Дополнительные классы
   */
  className: PropTypes.string,

  /**
   * Правила валидации для react-hook-form
   */
  rules: PropTypes.object,

  /**
   * Цвет стрелки
   */
  arrowColor: PropTypes.string,
};

export default FormSelect;
